import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronRight } from 'react-feather';
import type { SegmentRollup, CategoryRollup, MatrixRollup } from '@trinity/chat/types/message-testing';
import { scoreColor, scoreBg, scoreBorder } from './ScoreBadge';
import type { TestableMessage } from './types';
import { toTitleCase } from '@trinity/chat-components/utils';

// ─── Dimension Label Helper ─────────────────────────────────────────────────

const DIMENSION_LABELS: Record<string, string> = {
  clarity: 'Clarity',
  relevance: 'Relevance',
  differentiating: 'Differentiating',
  motivating: 'Motivating',
  credibility: 'Credibility',
};

function dimensionLabel(dimension: string): string {
  return DIMENSION_LABELS[dimension] ?? dimension;
}

// ─── Exported Tab Content Components ────────────────────────────────────────

export function OverallRollupContent({
  rollup,
  messages,
}: {
  rollup: MatrixRollup;
  messages: TestableMessage[];
}): React.JSX.Element {
  const msgMap = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

  return (
    <div>
      <HealthScoreBadge score={rollup.portfolioHealthScore}>
        <HealthDot $score={rollup.portfolioHealthScore} />
        <HealthLabel>Portfolio Health ({rollup.portfolioHealthScore})</HealthLabel>
      </HealthScoreBadge>

      <Section>
        <SectionTitle>Executive Summary</SectionTitle>
        <SectionText>{rollup.executiveSummary}</SectionText>
      </Section>

      {rollup.dimensionInsights.length > 0 && (
        <Section>
          <SectionTitle>Dimension Insights</SectionTitle>
          {rollup.dimensionInsights.map((d) => (
            <DimensionRow key={d.dimension}>
              <DimensionName>{dimensionLabel(d.dimension)}</DimensionName>
              <DimensionVerdict>{toTitleCase(d.verdict.replace(/_/g, ' '))}</DimensionVerdict>
              <DimensionAssessment>{d.assessment}</DimensionAssessment>
            </DimensionRow>
          ))}
        </Section>
      )}

      {rollup.universalWinners.length > 0 && (
        <CollapsibleSection>
          <CollapsibleSummary>Universal Winners</CollapsibleSummary>
          <MessageRefList>
            {rollup.universalWinners.map((m) => (
              <MessageRefCard key={m.messageId}>
                <MessageRefHeader>{msgMap.get(m.messageId)?.displayTitle ?? m.messageId}</MessageRefHeader>
                <MessageQuote $accent="#86efac">{msgMap.get(m.messageId)?.body}</MessageQuote>
                <MessageRefWhy>{m.why}</MessageRefWhy>
              </MessageRefCard>
            ))}
          </MessageRefList>
        </CollapsibleSection>
      )}

      {rollup.universalLosers.length > 0 && (
        <CollapsibleSection>
          <CollapsibleSummary>Universal Losers</CollapsibleSummary>
          <MessageRefList>
            {rollup.universalLosers.map((m) => (
              <MessageRefCard key={m.messageId}>
                <MessageRefHeader>{msgMap.get(m.messageId)?.displayTitle ?? m.messageId}</MessageRefHeader>
                <MessageQuote $accent="#fca5a5">{msgMap.get(m.messageId)?.body}</MessageQuote>
                <MessageRefWhy>{m.why}</MessageRefWhy>
              </MessageRefCard>
            ))}
          </MessageRefList>
        </CollapsibleSection>
      )}

      {rollup.crossSegmentPatterns.length > 0 && (
        <CollapsibleSection>
          <CollapsibleSummary>Cross-Segment Patterns</CollapsibleSummary>
          <PatternList>
            {rollup.crossSegmentPatterns.map((p, i) => (
              <PatternItem key={i}>{p}</PatternItem>
            ))}
          </PatternList>
        </CollapsibleSection>
      )}

      {rollup.crossCategoryPatterns.length > 0 && (
        <CollapsibleSection>
          <CollapsibleSummary>Cross-Category Patterns</CollapsibleSummary>
          <PatternList>
            {rollup.crossCategoryPatterns.map((p, i) => (
              <PatternItem key={i}>{p}</PatternItem>
            ))}
          </PatternList>
        </CollapsibleSection>
      )}

      {rollup.strategicRecommendations.length > 0 && (
        <CollapsibleSection>
          <CollapsibleSummary>Strategic Recommendations</CollapsibleSummary>
          <RecommendationList>
            {rollup.strategicRecommendations.map((r, i) => (
              <RecommendationCard key={i}>
                <RecHeader>
                  <PriorityBadge priority={r.priority}>{r.priority}</PriorityBadge>
                  <RecAction>{r.action}</RecAction>
                </RecHeader>
                <RecDetail>{r.rationale}</RecDetail>
                <RecImpact>Impact: {r.impactEstimate}</RecImpact>
              </RecommendationCard>
            ))}
          </RecommendationList>
        </CollapsibleSection>
      )}

      {rollup.priorityActionItems.length > 0 && (
        <CollapsibleSection>
          <CollapsibleSummary>Priority Action Items</CollapsibleSummary>
          <ActionItemList>
            {rollup.priorityActionItems.map((a, i) => (
              <ActionItem key={i}>
                <UrgencyBadge urgency={a.urgency}>{a.urgency.replace('_', ' ')}</UrgencyBadge>
                <span>{a.action}</span>
              </ActionItem>
            ))}
          </ActionItemList>
        </CollapsibleSection>
      )}
    </div>
  );
}

// ─── Segments Tab ───────────────────────────────────────────────────────────

export function SegmentRollupsContent({ rollups }: { rollups: SegmentRollup[] }): React.JSX.Element {
  return (
    <CardGrid>
      {rollups.map((r) => (
        <SegmentCard key={r.segmentIdentifier} rollup={r} />
      ))}
    </CardGrid>
  );
}

function SegmentCard({ rollup }: { rollup: SegmentRollup }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <CardHeader onClick={() => setExpanded(!expanded)}>
        <CardTitle>{rollup.segmentName}</CardTitle>
        <ExpandIcon>{expanded ? '▾' : '▸'}</ExpandIcon>
      </CardHeader>

      <Section>
        <SectionText>{rollup.executiveSummary}</SectionText>
      </Section>

      {expanded && (
        <>
          {rollup.dimensionInsights.length > 0 && (
            <Section>
              <SectionTitle>Dimension Insights</SectionTitle>
              {rollup.dimensionInsights.map((d) => (
                <DimensionRow key={d.dimension}>
                  <DimensionName>{dimensionLabel(d.dimension)}</DimensionName>
                  <DimensionVerdict>{d.verdict.replace(/_/g, ' ')}</DimensionVerdict>
                  <DimensionAssessment>{d.assessment}</DimensionAssessment>
                </DimensionRow>
              ))}
            </Section>
          )}

          <TwoColumn>
            <div>
              <SectionTitle>Key Strengths</SectionTitle>
              <PatternList>
                {rollup.keyStrengths.map((s, i) => (
                  <StrengthItem key={i}>{s}</StrengthItem>
                ))}
              </PatternList>
            </div>
            <div>
              <SectionTitle>Key Weaknesses</SectionTitle>
              <PatternList>
                {rollup.keyWeaknesses.map((w, i) => (
                  <WeaknessItem key={i}>{w}</WeaknessItem>
                ))}
              </PatternList>
            </div>
          </TwoColumn>

          {rollup.categoryPerformanceRanking.length > 0 && (
            <Section>
              <SectionTitle>Category Performance</SectionTitle>
              <CategoryRankingTable>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Score</th>
                    <th>Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.categoryPerformanceRanking.map((c) => (
                    <tr key={c.category}>
                      <td>{c.category}</td>
                      <td style={{ color: scoreColor(c.meanComposite) }}>{c.meanComposite.toFixed(1)}</td>
                      <td>{c.verdict}</td>
                    </tr>
                  ))}
                </tbody>
              </CategoryRankingTable>
            </Section>
          )}

          <RecommendationsSection recommendations={rollup.recommendations} />
        </>
      )}
    </Card>
  );
}

// ─── Categories Tab ─────────────────────────────────────────────────────────

export function CategoryRollupsContent({
  rollups,
  messages,
}: {
  rollups: CategoryRollup[];
  messages: TestableMessage[];
}): React.JSX.Element {
  return (
    <CardGrid>
      {rollups.map((r) => (
        <CategoryCard key={r.category} rollup={r} messages={messages} />
      ))}
    </CardGrid>
  );
}

function CategoryCard({
  rollup,
  messages,
}: {
  rollup: CategoryRollup;
  messages: TestableMessage[];
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const msgMap = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

  return (
    <Card>
      <CardHeader onClick={() => setExpanded(!expanded)}>
        <CardTitle>{rollup.category}</CardTitle>
        <ExpandIcon>{expanded ? '▾' : '▸'}</ExpandIcon>
      </CardHeader>

      <Section>
        <SectionText>{rollup.executiveSummary}</SectionText>
      </Section>

      {expanded && (
        <>
          <Section>
            <SectionTitle>Cross-Persona Analysis</SectionTitle>
            <SectionText>{rollup.crossPersonaAnalysis}</SectionText>
          </Section>

          {rollup.dimensionInsights.length > 0 && (
            <Section>
              <SectionTitle>Dimension Insights</SectionTitle>
              {rollup.dimensionInsights.map((d) => (
                <DimensionRow key={d.dimension}>
                  <DimensionName>{dimensionLabel(d.dimension)}</DimensionName>
                  <DimensionVerdict>{d.verdict.replace(/_/g, ' ')}</DimensionVerdict>
                  <DimensionAssessment>{d.assessment}</DimensionAssessment>
                </DimensionRow>
              ))}
            </Section>
          )}

          <TwoColumn>
            {rollup.bestMessages.length > 0 && (
              <div>
                <SectionTitle>Best Messages</SectionTitle>
                <MessageRefList>
                  {rollup.bestMessages.map((m) => (
                    <MessageRefCard key={m.messageId}>
                      <MessageRefHeader>
                        {msgMap.get(m.messageId)?.displayTitle ?? m.messageId}
                      </MessageRefHeader>
                      <MessageQuote $accent="#86efac">{msgMap.get(m.messageId)?.body}</MessageQuote>
                      <MessageRefWhy>{m.why}</MessageRefWhy>
                    </MessageRefCard>
                  ))}
                </MessageRefList>
              </div>
            )}
            {rollup.worstMessages.length > 0 && (
              <div>
                <SectionTitle>Worst Messages</SectionTitle>
                <MessageRefList>
                  {rollup.worstMessages.map((m) => (
                    <MessageRefCard key={m.messageId}>
                      <MessageRefHeader>
                        {msgMap.get(m.messageId)?.displayTitle ?? m.messageId}
                      </MessageRefHeader>
                      <MessageQuote $accent="#fca5a5">{msgMap.get(m.messageId)?.body}</MessageQuote>
                      <MessageRefWhy>{m.why}</MessageRefWhy>
                    </MessageRefCard>
                  ))}
                </MessageRefList>
              </div>
            )}
          </TwoColumn>

          {rollup.personaInsights.length > 0 && (
            <Section>
              <SectionTitle>Persona Insights</SectionTitle>
              {rollup.personaInsights.map((p) => (
                <PersonaInsightRow key={p.segmentIdentifier}>
                  <PersonaName>{p.segmentName}</PersonaName>
                  <PersonaFit>{p.fitAssessment}</PersonaFit>
                </PersonaInsightRow>
              ))}
            </Section>
          )}

          <RecommendationsSection recommendations={rollup.recommendations} />
        </>
      )}
    </Card>
  );
}

// ─── Shared Recommendations ─────────────────────────────────────────────────

function RecommendationsSection({
  recommendations,
}: {
  recommendations: Array<{ action: string; rationale: string; priority: 'high' | 'medium' | 'low' }>;
}): React.JSX.Element | null {
  if (recommendations.length === 0) return null;

  return (
    <Section>
      <SectionTitle>Recommendations</SectionTitle>
      <RecommendationList>
        {recommendations.map((r, i) => (
          <RecommendationCard key={i}>
            <RecHeader>
              <PriorityBadge priority={r.priority}>{r.priority}</PriorityBadge>
              <RecAction>{r.action}</RecAction>
            </RecHeader>
            <RecDetail>{r.rationale}</RecDetail>
          </RecommendationCard>
        ))}
      </RecommendationList>
    </Section>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const Section = styled.div`
  margin-bottom: 14px;
`;

const CollapsibleSection = styled.details`
  margin-bottom: 14px;
`;

const StyledSummary = styled.summary`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
  display: flex;
  align-items: center;
  list-style: none;
  border-bottom: 1px solid #eee;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    color: #374151;
  }
`;

const SummaryChevron = styled(ChevronRight)`
  margin-left: auto;
  flex-shrink: 0;
  color: #9ca3af;
  transition: transform 0.15s ease;

  details[open] > summary > & {
    transform: rotate(90deg);
  }
`;

function CollapsibleSummary({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <StyledSummary>
      {children}
      <SummaryChevron size={14} />
    </StyledSummary>
  );
}

const SectionTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin: 0 0 6px;
`;

const SectionText = styled.p`
  font-size: 13px;
  color: #374151;
  margin: 0;
  line-height: 1.5;
`;

// ─── Health Score ───────────────────────────────────────────────────────────

const HealthScoreBadge = styled.div<{ score: number }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: ${({ score }) => scoreBg(score)};
  border: 1px solid ${({ score }) => scoreBorder(score)};
`;

const HealthLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #374151;
`;

const HealthDot = styled.div<{ $score: number }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $score }) => scoreColor($score)};
  flex-shrink: 0;
`;

// ─── Cards ──────────────────────────────────────────────────────────────────

const CardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  background: #fafbfc;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  margin-bottom: 8px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #1f2937;
`;

const ExpandIcon = styled.span`
  font-size: 12px;
  color: #9ca3af;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 14px;
`;

// ─── Lists ──────────────────────────────────────────────────────────────────

const PatternList = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
`;

const PatternItem = styled.li``;

const StrengthItem = styled.li`
  color: #065f46;
`;

const WeaknessItem = styled.li`
  color: #991b1b;
`;

// ─── Message References ─────────────────────────────────────────────────────

const MessageRefList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MessageRef = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  line-height: 1.4;
`;

const MessageRefCard = styled.div`
  background: #fafafa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MessageRefHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
`;

const MessageQuote = styled.blockquote<{ $accent?: string }>`
  margin: 0;
  padding: 4px 10px;
  border-left: 3px solid ${(p) => p.$accent ?? '#d1d5db'};
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  font-style: italic;
`;

const MessageRefWhy = styled.div`
  font-size: 12px;
  color: #374151;
  line-height: 1.4;
`;

const MessageRefId = styled.code`
  font-size: 11px;
  background: #f3f4f6;
  padding: 1px 5px;
  border-radius: 3px;
  color: #6366f1;
  white-space: nowrap;
  flex-shrink: 0;
`;

// ─── Dimension Insights ─────────────────────────────────────────────────────

const DimensionRow = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

const DimensionName = styled.span`
  font-weight: 600;
  color: #1f2937;
  margin-right: 8px;
`;

const DimensionVerdict = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  text-transform: capitalize;
  margin-right: 8px;
`;

const DimensionAssessment = styled.div`
  color: #374151;
  margin-top: 2px;
  line-height: 1.4;
`;

// ─── Category Ranking Table ─────────────────────────────────────────────────

const CategoryRankingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-weight: 600;
    color: #6b7280;
    padding: 4px 8px;
    border-bottom: 1px solid #e5e7eb;
  }

  td {
    padding: 4px 8px;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
  }
`;

// ─── Persona Insights ───────────────────────────────────────────────────────

const PersonaInsightRow = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

const PersonaName = styled.span`
  font-weight: 600;
  color: #1f2937;
  margin-right: 8px;
`;

const PersonaFit = styled.span`
  color: #374151;
`;

// ─── Recommendations ────────────────────────────────────────────────────────

const RecommendationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecommendationCard = styled.div`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
`;

const RecHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const PriorityBadge = styled.span<{ priority: 'high' | 'medium' | 'low' }>`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 3px;
  color: ${({ priority }) =>
    priority === 'high' ? '#991b1b' : priority === 'medium' ? '#92400e' : '#065f46'};
  background: ${({ priority }) =>
    priority === 'high' ? '#fef2f2' : priority === 'medium' ? '#fffbeb' : '#ecfdf5'};
`;

const RecAction = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
`;

const RecDetail = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
`;

const RecImpact = styled.p`
  font-size: 12px;
  color: #4b5563;
  margin: 4px 0 0;
  font-style: italic;
`;

// ─── Action Items ───────────────────────────────────────────────────────────

const ActionItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ActionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
`;

const UrgencyBadge = styled.span<{ urgency: 'immediate' | 'short_term' | 'long_term' }>`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
  color: ${({ urgency }) =>
    urgency === 'immediate' ? '#991b1b' : urgency === 'short_term' ? '#92400e' : '#065f46'};
  background: ${({ urgency }) =>
    urgency === 'immediate' ? '#fef2f2' : urgency === 'short_term' ? '#fffbeb' : '#ecfdf5'};
`;
